import React, { useMemo, useState } from 'react';
import { useAppNavigate } from '@/navigation';
import {
  clearSelectedCredentialProfile,
  getCredentialProfilesFromFile,
  getSelectedCredentialProfile,
  setSelectedCredentialProfile,
} from './devCredentials';
import {
  clearAllFeatureFlagOverrides,
  featureFlags,
  getFeatureFlag,
  setFeatureFlagOverride,
  type FeatureFlagName,
} from '@/config/featureFlags';
import { cn } from '@/libs';
import { store } from '@/store';
import { AlertMessage, Button, CheckGroup, Dropdown, Icon } from '@/ui-kit';
import { DevSignedPdfSection } from './DevSignedPdfSection';
import type { DropdownDatalistOption } from '@/ui-kit/dropdown/dropdown';
import './DevDebugPanel.css';
import { isDevToolsEnabled } from '@/config/config';
import { ROUTES } from '@/routes/routes';

const isDev = import.meta.env.MODE === 'development';

type DevPanelAnchor = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

const DEV_PANEL_ANCHOR_STORAGE_KEY = 'devDebugPanel.anchor';

export const DevDebugPanel: React.FC = () => {
  const navigate = useAppNavigate();
  const profiles = useMemo(() => getCredentialProfilesFromFile(), []);
  const current = getSelectedCredentialProfile();

  const [selectedId, setSelectedId] = useState<string>(
    current?.id ?? profiles[0]?.id ?? '',
  );
  const [isOpen, setIsOpen] = useState(false);
  const [tick, setTick] = useState(0);
  const [anchor, setAnchor] = useState<DevPanelAnchor>(() => {
    const v = localStorage.getItem(DEV_PANEL_ANCHOR_STORAGE_KEY);
    if (
      v === 'bottom-right' ||
      v === 'bottom-left' ||
      v === 'top-right' ||
      v === 'top-left'
    ) {
      return v;
    }
    return 'bottom-right';
  });

  const profileDatalist: DropdownDatalistOption[] = useMemo(
    () =>
      profiles.map(p => ({
        value: p.label,
        key: p.id,
        selected: p.id === selectedId,
        disabled: false,
      })),
    [profiles, selectedId],
  );

  // tick is only used to force a rerender when localStorage changes
  void tick;

  if (!isDev || !isDevToolsEnabled) return null;

  const selectedProfile = profiles.find(p => p.id === selectedId);

  const anchorDatalist: DropdownDatalistOption[] = [
    {
      key: 'bottom-right',
      value: 'Bottom right',
      selected: anchor === 'bottom-right',
      disabled: false,
    },
    {
      key: 'bottom-left',
      value: 'Bottom left',
      selected: anchor === 'bottom-left',
      disabled: false,
    },
    {
      key: 'top-right',
      value: 'Top right',
      selected: anchor === 'top-right',
      disabled: false,
    },
    {
      key: 'top-left',
      value: 'Top left',
      selected: anchor === 'top-left',
      disabled: false,
    },
  ];

  const anchorValueLabel =
    anchor === 'bottom-right'
      ? 'Bottom right'
      : anchor === 'bottom-left'
        ? 'Bottom left'
        : anchor === 'top-right'
          ? 'Top right'
          : 'Top left';

  const anchorSideClass = anchor.endsWith('left')
    ? 'devDebugPanel--left'
    : 'devDebugPanel--right';
  const anchorVerticalClosedClass = anchor.startsWith('top')
    ? 'devDebugPanel--closedTop'
    : 'devDebugPanel--closedBottom';

  const flagChoices = (Object.keys(featureFlags) as FeatureFlagName[]).map(
    name => ({
      key: name,
      value: name,
      status: getFeatureFlag(name)
        ? ('checked' as const)
        : ('unchecked' as const),
      disabled: false,
    }),
  );

  const forceRefresh = () => setTick(t => t + 1);

  const toggleFlag = (name: FeatureFlagName) => {
    const next = !getFeatureFlag(name);
    setFeatureFlagOverride(name, next);
    forceRefresh();
  };

  return (
    <div
      className={cn(
        'devDebugPanel',
        anchorSideClass,
        isOpen ? 'devDebugPanel--open' : anchorVerticalClosedClass,
      )}
    >
      <div className={cn('devDebugPanel__header')}>
        <span className={cn('devDebugPanel__title')}>Dev panel</span>
        <Button
          variant="tertiary"
          {...(isOpen ? { asRemoveIcon: 'neutral' } : {})}
          kind="muted"
          size="small"
          aria-expanded={isOpen}
          aria-label={isOpen ? 'Hide dev panel' : 'Show dev panel'}
          leadingIcon={
            <Icon
              type="menu"
              size="medium"
              color="var(--color_neutral-darkest)"
            />
          }
          onClick={() => setIsOpen(v => !v)}
        />
      </div>

      {isOpen && (
        <div className={cn('devDebugPanel__body')}>
          <div className={cn('devDebugPanel__stack')}>
            <div>
              <div className={cn('devDebugPanel__sectionTitle')}>Panel</div>
              <Dropdown
                label="Anchor"
                name="devDebugPanelAnchor"
                id="devDebugPanelAnchor"
                as="select"
                variant="simple"
                includeClearIcon={false}
                value={anchorValueLabel}
                datalist={{ options: anchorDatalist }}
                onToggle={() => {}}
                onChange={e => {
                  const v = e.target.value?.trim();
                  if (!v) return;
                  const next = v as DevPanelAnchor;
                  if (
                    next !== 'bottom-right' &&
                    next !== 'bottom-left' &&
                    next !== 'top-right' &&
                    next !== 'top-left'
                  ) {
                    return;
                  }
                  setAnchor(next);
                  localStorage.setItem(DEV_PANEL_ANCHOR_STORAGE_KEY, next);
                  forceRefresh();
                }}
              />
            </div>

            <hr />

            <div>
              <div className={cn('devDebugPanel__sectionTitle')}>
                Credentials (from file → localStorage)
              </div>

              {profiles.length === 0 ? (
                <AlertMessage
                  variant="neutral"
                  title="No profiles found"
                  description="Create a local (gitignored) file at src/dev-secrets.credentials.json based on the example file."
                />
              ) : (
                <div className={cn('devDebugPanel__col')}>
                  <Dropdown
                    label="Credential profile"
                    name="devDebugCredentialProfile"
                    id="devDebugCredentialProfile"
                    as="select"
                    variant="simple"
                    includeClearIcon={false}
                    value={selectedProfile?.label ?? ''}
                    datalist={{ options: profileDatalist }}
                    onToggle={() => {}}
                    onChange={e => {
                      const v = e.target.value?.trim();
                      if (!v) return;
                      setSelectedId(v);
                    }}
                  />

                  <div className={cn('devDebugPanel__row')}>
                    <Button
                      variant="primary"
                      size="regular"
                      className={cn('devDebugPanel__grow')}
                      onClick={() => {
                        if (!selectedProfile) return;
                        setSelectedCredentialProfile(selectedProfile);
                        forceRefresh();

                        const shortlink = selectedProfile.shortlink?.trim();
                        if (!shortlink) return;
                        navigate(ROUTES.shortener.replace(':code', shortlink), {
                          replace: true,
                        });
                      }}
                    >
                      Apply
                    </Button>
                    <Button
                      variant="secondary"
                      size="regular"
                      onClick={() => {
                        clearSelectedCredentialProfile();
                        forceRefresh();
                      }}
                    >
                      Clear
                    </Button>
                  </div>

                  <div className={cn('devDebugPanel__mutedText')}>
                    Current:{' '}
                    <span className={cn('devDebugPanel__currentLabel')}>
                      {getSelectedCredentialProfile()?.label ?? 'None'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <hr />

            <div>
              <div className={cn('devDebugPanel__sectionTitle')}>
                Feature flags (local overrides)
              </div>

              <div className={cn('devDebugPanel__col')}>
                <CheckGroup
                  group="dev-feature-flags"
                  size="small"
                  orientation="column"
                  choices={flagChoices}
                  onOptionChange={c => {
                    toggleFlag(c.key as FeatureFlagName);
                  }}
                />

                <Button
                  variant="secondary"
                  size="regular"
                  className={cn('devDebugPanel__fullWidth')}
                  onClick={() => {
                    clearAllFeatureFlagOverrides();
                    forceRefresh();
                  }}
                >
                  Clear flag overrides
                </Button>
              </div>
            </div>

            <hr />

            <div>
              <div className={cn('devDebugPanel__sectionTitle')}>
                Redux store
              </div>
              <Button
                variant="secondary"
                size="regular"
                className={cn('devDebugPanel__fullWidth')}
                onClick={() => {
                  void (async () => {
                    await store.persistor?.flush();
                    forceRefresh();
                  })();
                }}
              >
                Flush persistence
              </Button>
              <div className={cn('devDebugPanel__mutedText')}>
                Writes the current in-memory state to persisted storage (e.g.
                localStorage) without logging out.
              </div>
            </div>

            <hr />

            <DevSignedPdfSection />
          </div>
        </div>
      )}
    </div>
  );
};
